import {
  REQUEST_CREATE_SEGMENT_GROUP,
  REQUEST_FETCH_SEGMENT_GROUPS,
  REQUEST_INACTIVATE_SEGMENT_GROUP,
  REQUEST_SORT_SEGMENT_GROUP,
  REQUEST_UPDATE_SEGMENT_GROUP,
} from 'actions/actionTypes';
import * as AppAction from 'actions/appActions';
import * as Action from 'actions/backend/segmentActions';
import * as FlashMessageAction from 'actions/flashMessageActions';
import * as API from 'apis/backend';
import { all, call, fork, put, select, take } from 'redux-saga/effects';
import { CallEffectInterface } from 'sagas';
import { RootState } from 'types';
import {
  SegmentGroupInputTypes,
  SegmentGroupSortParamsTypes,
  SegmentGroupTypes,
  SegmentGroupUpdateTypes,
} from 'types/backend/segment';

// Selector
const appSelector = (state: RootState) => state.app;
const backendCompanySelector = (state: RootState) => state.backend.company;

function* runRequestSortSegmentGroup(params: SegmentGroupSortParamsTypes) {
  const { rootPath, xCSRFToken }: ReturnType<typeof appSelector> = yield select(appSelector);
  const { currentCompany }: ReturnType<typeof backendCompanySelector> =
    yield select(backendCompanySelector);

  const { payload, error, newXCSRFToken }: CallEffectInterface<SegmentGroupTypes[]> = yield call(
    API.requestSortSegmentGroup,
    rootPath,
    currentCompany,
    xCSRFToken,
    params,
  );

  if (payload && newXCSRFToken && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successSortSegmentGroup(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: '項目を並び替えました。',
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureSortSegmentGroup()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: '項目の並び替えに失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestSortSegmentGroup() {
  while (true) {
    const { params } = yield take(REQUEST_SORT_SEGMENT_GROUP);
    yield fork(runRequestSortSegmentGroup, params);
  }
}

function* runRequestFetchSegmentGroups() {
  const { rootPath }: ReturnType<typeof appSelector> = yield select(appSelector);
  const { currentCompany }: ReturnType<typeof backendCompanySelector> =
    yield select(backendCompanySelector);

  const { payload, error, newXCSRFToken }: CallEffectInterface<SegmentGroupTypes[]> = yield call(
    API.requestFetchSegmentGroups,
    rootPath,
    currentCompany,
  );

  if (payload && newXCSRFToken && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successFetchSegmentGroups(payload)),
    ]);
  } else {
    yield put(Action.failureFetchSegmentGroups());
  }
}

function* handleRequestFetchSegmentGroups() {
  while (true) {
    yield take(REQUEST_FETCH_SEGMENT_GROUPS);
    yield fork(runRequestFetchSegmentGroups);
  }
}

function* runRequestCreateSegmentGroup(params: SegmentGroupInputTypes) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);
  const { payload, error, newXCSRFToken }: CallEffectInterface<SegmentGroupTypes> = yield call(
    API.requestCreateSegmentGroup,
    rootPath,
    currentCompany,
    params,
    xCSRFToken,
  );

  if (payload && newXCSRFToken && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successCreateSegmentGroup(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: `${params.name} を作成しました。`,
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureCreateSegmentGroup()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: 'セグメントグループの作成に失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestCreateSegmentGroup() {
  while (true) {
    const { params } = yield take(REQUEST_CREATE_SEGMENT_GROUP);
    yield fork(runRequestCreateSegmentGroup, params);
  }
}

function* runRequestUpdateSegmentGroup(params: SegmentGroupUpdateTypes) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);

  const { payload, error, newXCSRFToken }: CallEffectInterface<SegmentGroupTypes> = yield call(
    API.requestUpdateSegmentGroup,
    rootPath,
    currentCompany,
    params,
    xCSRFToken,
  );

  if (payload && newXCSRFToken && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successUpdateSegmentGroup(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: `「${params.name}」に変更しました。`,
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureUpdateSegmentGroup()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: 'セグメントグループの変更に失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestUpdateSegmentGroup() {
  while (true) {
    const { params } = yield take(REQUEST_UPDATE_SEGMENT_GROUP);
    yield fork(runRequestUpdateSegmentGroup, params);
  }
}

function* runRequestInactivateSegmentGroup(segmentGroupId: string) {
  const { rootPath, xCSRFToken } = yield select((state) => state.app);
  const { currentCompany } = yield select((state) => state.backend.company);

  const { payload, error, newXCSRFToken }: CallEffectInterface<SegmentGroupTypes> = yield call(
    API.requestInactivateSegmentGroup,
    rootPath,
    currentCompany,
    segmentGroupId,
    xCSRFToken,
  );

  if (payload && newXCSRFToken && !error) {
    yield all([
      put(AppAction.setXCSRFToken(newXCSRFToken)),
      put(Action.successInactivateSegmentGroup(payload)),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'success',
          message: `${payload.name}を削除しました。`,
        }),
      ),
    ]);
  } else {
    yield all([
      put(Action.failureInactivateSegmentGroup()),
      put(
        FlashMessageAction.addFlashMessage({
          type: 'error',
          message: 'セグメントグループの削除に失敗しました。',
        }),
      ),
    ]);
  }
}

function* handleRequestInactivateSegmentGroup() {
  while (true) {
    const { segmentGroupId } = yield take(REQUEST_INACTIVATE_SEGMENT_GROUP);
    yield fork(runRequestInactivateSegmentGroup, segmentGroupId);
  }
}

export default function* segmentSaga() {
  yield fork(handleRequestFetchSegmentGroups);
  yield fork(handleRequestCreateSegmentGroup);
  yield fork(handleRequestUpdateSegmentGroup);
  yield fork(handleRequestInactivateSegmentGroup);
  yield fork(handleRequestSortSegmentGroup);
}
